import { Details, Flex, Confirmation, Toaster } from '@ui'
import React, { useState } from 'react'
import { Button, Intent } from '@blueprintjs/core'
import api, { TicketPriority, TicketStatus, FormErrors, ITicketDetails } from '@api'
import { FormTicket } from '@api/ticket/create'
import { rows } from './rows'
import { omit } from 'lodash'
import { ApolloError } from 'apollo-boost'

export function NewTicketForm({
  dirty,
  setDirty,
  onCancel,
  onSuccess,
}: {
  dirty: boolean
  setDirty: React.Dispatch<React.SetStateAction<boolean>>
  onCancel: () => void
  onSuccess: (ticket: ITicketDetails) => any
}) {
  const newTicket = {
    shortDescription: '',
    description: '',
    status: TicketStatus.NEW,
    severity: TicketPriority.MEDIUM,
    assignedTo: null,
    building: null,
    floor_plan: null,
    dueDate: null,
  }

  return (
    <TicketForm
      dirty={dirty}
      setDirty={setDirty}
      ticket={newTicket}
      onCancel={onCancel}
      onSubmit={(ticket) =>
        api.ticket
          .create(ticket)
          .then((ticket) => onSuccess(ticket))
          .catch((error: ApolloError) => Toaster.fail({ error }))
      }
    />
  )
}

interface TicketFormProps {
  dirty: boolean
  ticket: FormTicket
  setDirty: (dirty: boolean) => void
  onCancel: () => void
  onSubmit: (ticket: FormTicket) => Promise<any>
}

export type TicketFormErrors = FormErrors<FormTicket>

interface RequiredField {
  key: 'shortDescription' | 'description'
  label: string
}

const requiredFields: RequiredField[] = [
  { key: 'shortDescription', label: 'Title' },
  { key: 'description', label: 'Description' },
]

export function TicketForm({
  dirty,
  setDirty,
  ticket: initState,
  onCancel,
  onSubmit,
}: TicketFormProps) {
  const [ticket, setTicket] = useState<FormTicket>(initState)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<TicketFormErrors>({})

  const update = (field: keyof FormTicket, val: any) => {
    setDirty && setDirty(true)
    setErrors(omit(errors, field))
    setTicket((prevState) => ({ ...prevState, [field]: val }))
  }

  function isValid(ticket: FormTicket) {
    let valid = true
    requiredFields.forEach(({ key }) => {
      if (valid) {
        valid = !!ticket[key]
      }
    })

    return valid
  }

  const handleErrors = ({ graphQLErrors }: ApolloError) => {
    setLoading(false)

    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: TicketFormErrors = graphQLErrors.reduce(
        (result: TicketFormErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const highlightErrors = () => {
    const foundErrors: TicketFormErrors = {}
    requiredFields.forEach((field) => {
      if (!ticket[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }
    })

    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const submit = () => {
    if (!isValid(ticket)) {
      highlightErrors()
      return
    }

    setLoading(true)
    onSubmit(ticket)
      .catch(handleErrors)
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <div style={{ paddingTop: 20 }}>
      <Details
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <>
          {rows.map(({ label, required, formEl }) => (
            <Details.Row key={label}>
              <Details.Label {...{ label, required }} />
              {formEl(ticket, update, errors)}
            </Details.Row>
          ))}
        </>
        <Details.Footer>
          <Flex align="center" justify="flex-end">
            <div style={{ marginRight: 8 }}>
              <Confirmation enabled={dirty} text="Discard unsaved changes?" onConfirm={onCancel}>
                <Button
                  disabled={loading}
                  type="button"
                  text="Cancel"
                  onClick={() => {
                    if (!dirty) onCancel()
                  }}
                />
              </Confirmation>
            </div>
            <Button
              disabled={!dirty}
              intent={Intent.PRIMARY}
              loading={loading}
              text={'id' in ticket ? 'Update' : 'Create'}
              type="submit"
              data-button={'id' in ticket ? 'update_ticket' : 'create_ticket'}
            />
          </Flex>
        </Details.Footer>
      </Details>
    </div>
  )
}
